import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useApprovedLeaves, useCreateMeeting, useDeleteMeeting, useEmployees, useMeetings, useShiftLocations, useShifts, useUpdateMeeting, type LeaveRequest } from '../api/hooks';
import type { Meeting, Shift } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { useLang } from '../i18n/LangProvider';
import { useConfirm } from '../ui/confirm';
import { Icon } from '../ui/Icon';
import { useDialogFocus } from '../ui/useDialogFocus';

type EventForm = { id: string | null; title: string; start: string; end: string; location: string; description: string; color: string; attendees: string[] };
const EMPTY_FORM: EventForm = { id: null, title: '', start: '09:00', end: '10:00', location: '', description: '', color: 'purple', attendees: [] };

const LEAVE_LABEL: Record<string, string> = { ferie: 'Ferie', permesso: 'Permesso', malattia: 'Malattia' };
const LEAVE_COLOR: Record<string, string> = { ferie: '#2b7bb9', permesso: '#e08a1e', malattia: '#c0392b' };

// Shift palette — kept in sync with the Turni screen so a sede reads the same colour everywhere.
const SHIFT_LOC_COLOR: Record<string, string> = { uff: '#e79aa6', vat: '#93cf8f', col: '#5bb8e6', nav: '#f2a63d', home: '#c3ace0', csam: '#9ad0c0', borgh: '#e6c34a' };
const SHIFT_STATE_LABEL: Record<string, string> = { off: 'OFF', standby: 'STAND-BY', ferie: 'FERIE', malattia: 'MALATTIA', permesso: 'PERMESSO' };
const SHIFT_YELLOW = '#ffe74d';
const shiftSegs = (s: Shift) => (s.segments?.length ? s.segments : (s.locationKey ? [{ loc: s.locationKey, range: s.range }] : [])).filter((x) => x?.loc);
const shiftState = (s: Shift) => (s.typeKey && SHIFT_STATE_LABEL[s.typeKey] ? SHIFT_STATE_LABEL[s.typeKey] : null);
const isRenderableShift = (s: Shift) => !!shiftState(s) || shiftSegs(s).length > 0;

const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const DOW = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const EVENT_COLORS: Record<string, string> = { orange: 'var(--t-orange)', magenta: 'var(--t-magenta)', purple: 'var(--t-purple)', info: 'var(--info,#2b7bb9)' };
const colorOf = (m: Meeting) => EVENT_COLORS[m.color ?? ''] ?? 'var(--t-purple)';

/** Builds a 6×7 grid of dates covering the month of `cursor` (Mon-first weeks). */
function monthGrid(cursor: Date): Date[] {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7; // Monday = 0
  const start = new Date(first);
  start.setDate(first.getDate() - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export function CalendarScreen() {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { lang, t } = useLang();
  const confirm = useConfirm();
  const loc = lang === 'en' ? 'en-GB' : 'it-IT';
  const auth = useAuth();
  const q = useMeetings();
  const createMeeting = useCreateMeeting();
  const updateMeeting = useUpdateMeeting();
  const deleteMeeting = useDeleteMeeting();
  const shiftsQ = useShifts();
  const locsQ = useShiftLocations();
  const employeesQ = useEmployees();
  const shiftShort = (k?: string | null) => locsQ.data?.find((l) => l.key === k)?.short ?? k?.toUpperCase() ?? '';

  // People you can invite, and a lookup to turn an attendee email into a name.
  const invitable = (employeesQ.data?.items ?? []).filter((e) => e.active && e.email !== auth.email);
  const nameByEmail = useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of employeesQ.data?.items ?? []) m[e.email] = e.name;
    return m;
  }, [employeesQ.data]);
  const isAdmin = auth.hasRole('admin');

  const [cursor, setCursor] = useState(() => new Date());
  const [selected, setSelected] = useState<string | null>(null);
  const [form, setForm] = useState<EventForm>(EMPTY_FORM);
  const closeModal = () => { setSelected(null); setForm(EMPTY_FORM); };
  const openNew = () => { setForm(EMPTY_FORM); setSelected(todayIso); };
  useDialogFocus(dialogRef, Boolean(selected), closeModal);

  const meetings = q.data?.items ?? [];
  const byDate = useMemo(() => {
    const map: Record<string, Meeting[]> = {};
    // The API may return `date` as a full ISO datetime; key on the calendar day only.
    for (const m of meetings) (map[m.date.slice(0, 10)] ??= []).push(m);
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.start.localeCompare(b.start));
    return map;
  }, [meetings]);

  const grid = useMemo(() => monthGrid(cursor), [cursor]);
  const todayIso = iso(new Date());
  const monthLabel = `${lang === 'en' ? cursor.toLocaleDateString('en-GB', { month: 'long' }) : MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;

  // Approved leaves overlaid on the grid (spanning start→end).
  const leavesQ = useApprovedLeaves(iso(grid[0]), iso(grid[grid.length - 1]));
  const leavesByDate = useMemo(() => {
    const map: Record<string, LeaveRequest[]> = {};
    for (const l of leavesQ.data ?? []) {
      const s = new Date(`${l.startDate.slice(0, 10)}T00:00:00`);
      const e = new Date(`${l.endDate.slice(0, 10)}T00:00:00`);
      for (const d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) (map[iso(d)] ??= []).push(l);
    }
    return map;
  }, [leavesQ.data]);

  // My own shift for a quick per-day glance in the grid…
  const myShiftByDate = useMemo(() => {
    const m: Record<string, Shift> = {};
    for (const s of shiftsQ.data ?? []) if (s.employeeId === auth.sub && isRenderableShift(s)) m[s.date.slice(0, 10)] = s;
    return m;
  }, [shiftsQ.data, auth.sub]);
  // …and the whole roster for the day detail.
  const shiftsByDate = useMemo(() => {
    const m: Record<string, Shift[]> = {};
    for (const s of shiftsQ.data ?? []) {
      if (!isRenderableShift(s)) continue;
      (m[s.date.slice(0, 10)] ??= []).push(s);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => (a.employee?.name ?? '').localeCompare(b.employee?.name ?? ''));
    return m;
  }, [shiftsQ.data]);

  const dayEvents = selected ? byDate[selected] ?? [] : [];
  const dayLeaves = selected ? leavesByDate[selected] ?? [] : [];
  const dayShifts = selected ? shiftsByDate[selected] ?? [] : [];

  const startEdit = (m: Meeting) => setForm({
    id: m.id, title: m.title, start: m.start, end: m.end,
    location: m.location ?? '', description: m.description ?? '', color: m.color ?? 'purple', attendees: m.attendees ?? [],
  });
  const toggleAttendee = (email: string) => setForm((f) => ({
    ...f, attendees: f.attendees.includes(email) ? f.attendees.filter((a) => a !== email) : [...f.attendees, email],
  }));

  async function saveEvent(e: FormEvent) {
    e.preventDefault();
    if (!selected || !form.title.trim()) return;
    const body = {
      title: form.title.trim(),
      date: selected,
      start: form.start,
      end: form.end,
      location: form.location.trim() || undefined,
      description: form.description.trim() || undefined,
      color: form.color,
      attendees: form.attendees,
    };
    if (form.id) await updateMeeting.mutateAsync({ id: form.id, ...body });
    else await createMeeting.mutateAsync(body);
    setForm(EMPTY_FORM);
  }

  return (
    <div className="calendar-screen" style={{ padding: 24 }}>
      <div className="page-head calendar-page-head" style={{ marginBottom: 16 }}>
        <div className="row calendar-page-nav" style={{ gap: 10, alignItems: 'center' }}>
          <button className="btn btn--ghost btn--icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} aria-label={t('Mese precedente')}><Icon name="chevL" size={15} /></button>
          <h1 className="h1 calendar-month" style={{ fontSize: 20, minWidth: 190, textAlign: 'center', textTransform: 'capitalize' }}>{monthLabel}</h1>
          <button className="btn btn--ghost btn--icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} aria-label={t('Mese successivo')}><Icon name="chevR" size={15} /></button>
          <button className="btn btn--sm" onClick={() => { setCursor(new Date()); }}>{t('Oggi')}</button>
        </div>
        <button className="btn btn--brand calendar-new-event" onClick={openNew}><Icon name="plus" size={14} /> {t('Nuovo evento')}</button>
      </div>

      <div className="calendar-month-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, background: 'var(--line,#eee)', border: '1px solid var(--line,#eee)', borderRadius: 10, overflow: 'hidden' }}>
        {DOW.map((d) => (
          <div className="calendar-month-grid__head" key={d} style={{ background: 'var(--bg-sunk,#f6f6f6)', padding: '8px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ink-3)' }}>{t(d)}</div>
        ))}
        {grid.map((d) => {
          const key = iso(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = key === todayIso;
          const events = byDate[key] ?? [];
          const leaves = leavesByDate[key] ?? [];
          return (
            <button
              className="calendar-day"
              key={key}
              onClick={() => setSelected(key)}
              style={{
                background: 'var(--bg,#fff)', minHeight: 96, padding: 6, textAlign: 'left', border: 'none', cursor: 'pointer',
                opacity: inMonth ? 1 : 0.4, outline: selected === key ? '2px solid var(--t-orange)' : 'none', outlineOffset: -2,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                <span style={{
                  fontSize: 12, fontWeight: 600, width: 22, height: 22, display: 'grid', placeItems: 'center', borderRadius: '50%',
                  background: isToday ? 'var(--t-orange)' : 'transparent', color: isToday ? '#fff' : 'var(--ink-2)',
                }}>{d.getDate()}</span>
              </div>
              <div className="calendar-day__items" style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {leaves.map((l) => (
                  <div className="calendar-day__item" key={l.id} title={`${LEAVE_LABEL[l.type]} · ${l.employee?.name ?? ''}`}
                    style={{ fontSize: 10.5, padding: '2px 6px', borderRadius: 5, background: `${LEAVE_COLOR[l.type]}22`, color: LEAVE_COLOR[l.type], fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', border: `1px solid ${LEAVE_COLOR[l.type]}55` }}>
                    {LEAVE_LABEL[l.type]}{l.partial ? ` ${l.startTime}` : ''} · {l.employee?.name?.split(' ')[0] ?? ''}
                  </div>
                ))}
                {events.slice(0, 3).map((m) => (
                  <div className="calendar-day__item" key={m.id} style={{ fontSize: 11, padding: '2px 6px', borderRadius: 5, background: colorOf(m), color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.start} {m.title}
                  </div>
                ))}
                {events.length > 3 && <div className="muted" style={{ fontSize: 10.5 }}>{t('+{n} altri', { n: events.length - 3 })}</div>}
                {(() => {
                  const my = myShiftByDate[key];
                  if (!my) return null;
                  const st = shiftState(my);
                  const segs = shiftSegs(my);
                  const bg = st ? SHIFT_YELLOW : (SHIFT_LOC_COLOR[segs[0].loc ?? ''] ?? '#c9c9c9');
                  const label = st ?? segs.map((s) => shiftShort(s.loc)).join('/');
                  return (
                    <div className="calendar-day__item calendar-day__shift" title={t('Il mio turno: {label}', { label })}
                      style={{ fontSize: 11, padding: '2px 6px', borderRadius: 5, background: bg, color: st ? '#5a4a00' : '#222', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {label}{!st && segs[0].range ? ` ${segs[0].range}` : ''}
                    </div>
                  );
                })()}
              </div>
            </button>
          );
        })}
      </div>

      {selected && (
        <div className="modal-backdrop" onClick={closeModal}>
        <div
          ref={dialogRef}
          className="modal card cal-modal calendar-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="calendar-modal-title"
          tabIndex={-1}
          style={{ padding: 0, maxWidth: 560, width: '100%' }}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="cal-modal__head calendar-modal__header">
            <strong id="calendar-modal-title" style={{ textTransform: 'capitalize' }}>
              {new Date(selected).toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'long' })}
            </strong>
            <button type="button" className="btn btn--ghost btn--icon" onClick={closeModal} aria-label={t('Chiudi')}><Icon name="x" size={15} /></button>
          </header>

          <div className="cal-modal__body calendar-modal__body">
            {/* Let the user move the event to another day without closing. */}
            <input
              type="date"
              data-dialog-initial-focus
              className="input calendar-modal__date"
              aria-label={t('Data evento')}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              style={{ width: 180 }}
            />

          {dayLeaves.length > 0 && (
            <div className="col calendar-modal__leaves" style={{ gap: 6 }}>
              {dayLeaves.map((l) => (
                <div key={l.id} className="row calendar-modal__leave" style={{ gap: 8, alignItems: 'center', padding: '7px 10px', borderRadius: 8, background: `${LEAVE_COLOR[l.type]}18` }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: LEAVE_COLOR[l.type] }} />
                  <span style={{ fontSize: 13 }}>
                    <strong>{t(LEAVE_LABEL[l.type])}{l.partial ? ` ${l.startTime}–${l.endTime}` : ''}</strong>
                    {l.employee && <span className="muted"> · {l.employee.name}</span>}
                  </span>
                </div>
              ))}
            </div>
          )}

          {dayShifts.length > 0 && (
            <div className="col calendar-modal__shifts" style={{ gap: 6 }}>
              <div className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{t('Turni del giorno')}</div>
              {dayShifts.map((s) => {
                const st = shiftState(s);
                const segs = shiftSegs(s);
                return (
                  <div key={s.id} className="row calendar-modal__shift" style={{ gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, flex: '0 0 auto', minWidth: 116 }}>{s.employee?.name ?? '—'}</span>
                    <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                      {st ? (
                        <span style={{ background: SHIFT_YELLOW, color: '#5a4a00', fontWeight: 700, fontSize: 11, borderRadius: 5, padding: '2px 7px' }}>{t(st)}</span>
                      ) : segs.map((seg, i) => (
                        <span key={i} style={{ background: SHIFT_LOC_COLOR[seg.loc ?? ''] ?? '#c9c9c9', color: '#222', fontWeight: 700, fontSize: 11, borderRadius: 5, padding: '2px 7px' }}>
                          {shiftShort(seg.loc)}{seg.range ? ` ${seg.range}` : ''}
                        </span>
                      ))}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="col calendar-modal__events" style={{ gap: 8 }}>
            {dayEvents.length === 0 && dayLeaves.length === 0 && dayShifts.length === 0 && <div className="muted" style={{ fontSize: 13 }}>{t('Nessun evento in questa giornata.')}</div>}
            {dayEvents.map((m) => {
              const canManage = m.ownerId === auth.sub || isAdmin;
              const invitedNames = (m.attendees ?? []).map((a) => nameByEmail[a] ?? a);
              return (
              <div key={m.id} className="spaced calendar-modal__event" style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--bg-sunk,#f6f6f6)' }}>
                <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: colorOf(m), marginTop: 5, flex: '0 0 auto' }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                      {m.title}
                      {!canManage && <span className="calendar-modal__invited-tag" title={m.owner ? t('Invitato da {name}', { name: m.owner.name }) : t('Invitato')}>{t('invitato')}</span>}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>{m.start}–{m.end}{m.location ? ` · ${m.location}` : ''}</div>
                    {invitedNames.length > 0 && (
                      <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}><Icon name="users" size={11} /> {invitedNames.join(', ')}</div>
                    )}
                  </div>
                </div>
                {canManage && (
                  <div className="row" style={{ gap: 2, flex: '0 0 auto' }}>
                    <button type="button" className="btn btn--ghost btn--icon" onClick={() => startEdit(m)} title={t('Modifica evento')} aria-label={t('Modifica {title}', { title: m.title })}>
                      <Icon name="edit" size={13} />
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--icon"
                      onClick={async () => { if (await confirm({ message: t('Eliminare l’evento “{title}”?', { title: m.title }), confirmLabel: t('Elimina'), danger: true })) deleteMeeting.mutate(m.id); }}
                      title={t('Elimina evento')}
                      aria-label={t('Elimina {title}', { title: m.title })}
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          <form id="calendar-event-form" className="calendar-modal__form" onSubmit={saveEvent} style={{ borderTop: '1px solid var(--line,#eee)', paddingTop: 12 }}>
            <div className="muted calendar-modal__form-title" style={{ fontSize: 12, marginBottom: 8, fontWeight: 600 }}>{form.id ? t('Modifica evento') : t('Aggiungi evento')}</div>
            <input className="input calendar-modal__title-input" placeholder={t('Titolo evento')} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required style={{ marginBottom: 8 }} />
            <div className="row calendar-modal__form-row" style={{ gap: 8, marginBottom: 8 }}>
              <input type="time" className="input calendar-modal__time-input" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} style={{ flex: 1 }} />
              <input type="time" className="input calendar-modal__time-input" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} style={{ flex: 1 }} />
              <input className="input calendar-modal__location-input" placeholder={t('Luogo')} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} style={{ flex: 2 }} />
            </div>
            <input className="input calendar-modal__description-input" placeholder={t('Descrizione (opzionale)')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ marginBottom: 8 }} />
            {invitable.length > 0 && (
              <details className="calendar-modal__invite">
                <summary>
                  <Icon name="users" size={13} /> {t('Invita persone')}{form.attendees.length ? ` · ${form.attendees.length}` : ''}
                </summary>
                <div className="calendar-modal__invite-list">
                  {invitable.map((e) => (
                    <label key={e.id} className="calendar-modal__invite-item">
                      <input type="checkbox" checked={form.attendees.includes(e.email)} onChange={() => toggleAttendee(e.email)} />
                      <span>{e.name}</span>
                    </label>
                  ))}
                </div>
              </details>
            )}
          </form>
          </div>

          <footer className="cal-modal__foot calendar-modal__footer">
            <div className="row calendar-modal__color-picker" style={{ gap: 6 }} role="group" aria-label={t('Colore evento')}>
              {Object.entries(EVENT_COLORS).map(([k, v]) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => setForm({ ...form, color: k })}
                  title={k}
                  aria-label={k}
                  aria-pressed={form.color === k}
                  style={{ width: 22, height: 22, borderRadius: '50%', background: v, border: form.color === k ? '2px solid var(--ink)' : '2px solid transparent', cursor: 'pointer' }}
                />
              ))}
            </div>
            <span className="cal-modal__foot-spacer calendar-modal__footer-spacer" />
            {form.id && (
              <button type="button" className="btn btn--sm" onClick={() => setForm(EMPTY_FORM)}>{t('Annulla')}</button>
            )}
            <button type="submit" form="calendar-event-form" className="btn btn--sm btn--brand calendar-modal__submit" disabled={createMeeting.isPending || updateMeeting.isPending}>
              <Icon name={form.id ? 'check' : 'plus'} size={13} /> {form.id ? t('Salva modifiche') : t('Aggiungi evento')}
            </button>
          </footer>
        </div>
        </div>
      )}
    </div>
  );
}
